import {
  // Admin hooks
  useConnectStediAdminMutation,
  // Provider hooks
  useConnectStediProviderMutation,
  useDisconnectStediAdminMutation,
  useDisconnectStediProviderMutation,
  useGetStediStatusAdminQuery,
  useGetStediStatusProviderQuery,
  useVerifyStediAdminMutation,
  useVerifyStediProviderMutation,
} from './api';

// ========================================
// Admin hooks (for JotPsych admin panel)
// ========================================

/**
 * Hook to get Stedi connection status for an organization (admin use)
 * @param organizationId - The organization ID to check status for
 */
export function useStediStatusAdmin(organizationId: string | null, options?: { skip?: boolean }) {
  const result = useGetStediStatusAdminQuery(
    { organizationId: organizationId! },
    {
      skip: !organizationId || options?.skip,
    },
  );

  return {
    status: result.data || null,
    isConnected: result.data?.connected || false,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook for Stedi connection operations (admin use)
 * Used by JotPsych admins to manage Stedi connections for any organization
 */
export function useStediConnectionAdmin() {
  const [connect, connectResult] = useConnectStediAdminMutation();
  const [verify, verifyResult] = useVerifyStediAdminMutation();
  const [disconnect, disconnectResult] = useDisconnectStediAdminMutation();

  return {
    // Connect mutation
    connect,
    connectLoading: connectResult.isLoading,
    connectError: connectResult.error,
    connectSuccess: connectResult.isSuccess,

    // Verify mutation
    verify,
    verifyLoading: verifyResult.isLoading,
    verifyError: verifyResult.error,
    verifySuccess: verifyResult.isSuccess,

    // Disconnect mutation
    disconnect,
    disconnectLoading: disconnectResult.isLoading,
    disconnectError: disconnectResult.error,
    disconnectSuccess: disconnectResult.isSuccess,
  };
}

// ========================================
// Provider hooks (for billing administrators)
// ========================================

/**
 * Hook to get Stedi connection status for the current organization (provider use)
 * No organizationId needed - uses the authenticated user's organization
 */
export function useStediStatusProvider(options?: { skip?: boolean }) {
  const result = useGetStediStatusProviderQuery(undefined, {
    skip: options?.skip,
  });

  return {
    status: result.data || null,
    isConnected: result.data?.connected || false,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook for Stedi connection operations (provider use)
 * Used by billing administrators to manage their own organization's Stedi connection
 */
export function useStediConnectionProvider() {
  const [connect, connectResult] = useConnectStediProviderMutation();
  const [verify, verifyResult] = useVerifyStediProviderMutation();
  const [disconnect, disconnectResult] = useDisconnectStediProviderMutation();

  return {
    // Connect mutation
    connect,
    connectLoading: connectResult.isLoading,
    connectError: connectResult.error,
    connectSuccess: connectResult.isSuccess,

    // Verify mutation
    verify,
    verifyLoading: verifyResult.isLoading,
    verifyError: verifyResult.error,
    verifySuccess: verifyResult.isSuccess,

    // Disconnect mutation
    disconnect,
    disconnectLoading: disconnectResult.isLoading,
    disconnectError: disconnectResult.error,
    disconnectSuccess: disconnectResult.isSuccess,
  };
}

// ========================================
// Legacy hooks (for backward compatibility)
// These use admin routes - TO BE DEPRECATED
// Migrate to useStediStatusAdmin/useStediStatusProvider
// ========================================

/**
 * @deprecated Use useStediStatusAdmin or useStediStatusProvider instead
 */
export function useStediStatus(organizationId: string | null, options?: { skip?: boolean }) {
  return useStediStatusAdmin(organizationId, options);
}

/**
 * @deprecated Use useStediConnectionAdmin or useStediConnectionProvider instead
 */
export function useStediConnection() {
  return useStediConnectionAdmin();
}
